<div class="page-container">
    <div fxLayout="column">
        <app-page-header [section]="'Atoku'" [title]="'Reports'"
            [description]="'Commercial kitchen and storage occupancy reporting.'"></app-page-header>

        <div class="page-padding page-content full-screen-height-page-default" fxLayout="column">

            <div class="table-card mat-elevation-z3 report-toolbar" fxLayout="column">
                <div fxLayout="row wrap" fxLayoutAlign="space-between center" fxLayoutGap="16px">
                    <div fxLayout="column" fxLayoutGap="6px">
                        <span class="heading-default">Reporting Overview</span>
                        <span class="subtitle-default">Occupancy reports</span>
                        <span class="body-1-regular-2" *ngIf="reports">Generated {{ generatedOnLabel }}</span>
                    </div>

                    <div class="toolbar-actions" fxLayout="row wrap" fxLayoutAlign="end center" fxLayoutGap="12px">
                        <mat-form-field class="custom-text-field year-field" appearance="outline" floatLabel="always"
                            *ngIf="availableYears.length > 0">
                            <mat-label>Report Year</mat-label>
                            <mat-select [value]="selectedYearValue" (selectionChange)="onYearChange($event.value)">
                                <mat-option *ngFor="let year of availableYears; trackBy: trackByIndex" [value]="year">
                                    {{ year }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field>

                        <button class="button-action export-button" mat-flat-button type="button"
                            (click)="exportToExcel()" [disabled]="isLoading || !reports">
                            <div class="button-text">Export Excel</div>
                        </button>

                        <button class="button-action export-button" mat-flat-button type="button"
                            (click)="downloadStatement()" [disabled]="isLoading || isStatementDownloading">
                            <div class="button-text">{{ isStatementDownloading ? 'Downloading' : 'Download Statement' }}</div>
                        </button>
                    </div>
                </div>
            </div>

            <div class="report-state table-card mat-elevation-z3" *ngIf="isLoading">
                <span class="body-1-regular-2">Loading reports...</span>
            </div>

            <div class="report-state table-card mat-elevation-z3" *ngIf="!isLoading && errorMessage">
                <span class="body-1-regular-2">{{ errorMessage }}</span>
            </div>

            <div class="table-card mat-elevation-z3 report-tabs-card" *ngIf="!isLoading && !errorMessage && reports"
                fxLayout="column">

                <mat-tab-group animationDuration="0ms">
                    <mat-tab label="Business Mix">
                        <div class="report-tab-body" fxLayout="column" fxLayoutGap="20px">
                            <div fxLayout="column" fxLayoutGap="6px">
                                <span class="heading-default">Pricing profile mix</span>
                                <span class="body-1-regular-2">Monthly booking split for {{ selectedYearValue }}, grouped by pricing profile.</span>
                                <span class="body-1-regular-2" *ngIf="unclassifiedBusinessTotal > 0">
                                    {{ unclassifiedBusinessTotal }} unclassified businesses were excluded from the percentage split.
                                </span>
                            </div>

                            <div class="table-wrap">
                                <table class="report-table">
                                    <thead>
                                        <tr>
                                            <th>Month</th>
                                            <th>Total Bookings</th>
                                            <th *ngFor="let pricingProfile of businessMixPricingProfiles; trackBy: trackByProfileCode">
                                                {{ pricingProfile.name }}
                                            </th>
                                            <th *ngFor="let pricingProfile of businessMixPricingProfiles; trackBy: trackByProfileCode">
                                                {{ pricingProfile.name }} %
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr *ngFor="let month of businessMixMonths; trackBy: trackByIndex">
                                            <th>{{ month.monthLabel }}</th>
                                            <td>{{ month.totalBookings }}</td>
                                            <td *ngFor="let pricingProfile of businessMixPricingProfiles; trackBy: trackByProfileCode">
                                                {{ getBusinessMixMetric(month, pricingProfile.code).businesses }}
                                            </td>
                                            <td *ngFor="let pricingProfile of businessMixPricingProfiles; trackBy: trackByProfileCode">
                                                {{ formatPercent(getBusinessMixMetric(month, pricingProfile.code).share) }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </mat-tab>

                    <mat-tab label="Kitchens">
                        <div class="report-tab-body" fxLayout="column" fxLayoutGap="20px">
                            <div fxLayout="column" fxLayoutGap="6px">
                                <span class="heading-default">Kitchen occupancy</span>
                                <span class="body-1-regular-2">Open a kitchen to view its workbook-style occupancy and year comparison tables.</span>
                            </div>

                            <mat-accordion [multi]="false" *ngIf="kitchenSections.length > 0">
                                <mat-expansion-panel *ngFor="let kitchen of kitchenSections; let kitchenIndex = index; trackBy: trackByIndex"
                                    [expanded]="kitchenIndex === 0">
                                    <mat-expansion-panel-header>
                                        <mat-panel-title>{{ kitchen.spaceName }}</mat-panel-title>
                                        <mat-panel-description>{{ kitchen.year }} · {{ kitchen.spaceCode }}</mat-panel-description>
                                    </mat-expansion-panel-header>

                                    <div class="panel-content" fxLayout="column" fxLayoutGap="20px">
                                        <div fxLayout="column" fxLayoutGap="6px">
                                            <span class="heading-default">Occupancy Sheet</span>
                                            <span class="body-1-regular-2">Weekly breakdown for {{ kitchen.year }}.</span>
                                        </div>

                                        <div class="report-state" *ngIf="kitchen.months.length === 0">
                                            <span class="body-1-regular-2">No kitchen occupancy data is available for {{ kitchen.year }}.</span>
                                        </div>

                                        <div class="table-wrap" *ngIf="kitchen.months.length > 0">
                                            <table class="report-table kitchen-table">
                                                <thead>
                                                    <tr>
                                                        <th>Month</th>
                                                        <th>Metric</th>
                                                        <th *ngFor="let weekHeader of weekHeaders; trackBy: trackByIndex">{{ weekHeader }}</th>
                                                        <th>Total</th>
                                                    </tr>
                                                </thead>

                                                <tbody *ngFor="let month of kitchen.months; trackBy: trackByIndex">
                                                    <tr *ngFor="let row of month.rows; let rowIndex = index; trackBy: trackByIndex">
                                                        <th *ngIf="rowIndex === 0" class="month-cell" [attr.rowspan]="month.rows.length">
                                                            {{ month.monthLabel }}
                                                        </th>

                                                        <th class="metric-cell">{{ row.label }}</th>

                                                        <td *ngFor="let value of row.weekValues; trackBy: trackByIndex">
                                                            {{ formatMetricValue(value, row.isHours) }}
                                                        </td>

                                                        <td class="total-cell">
                                                            {{ formatMetricValue(row.total, row.isHours) }}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>

                                        <div fxLayout="column" fxLayoutGap="6px">
                                            <span class="heading-default">Year Comparison</span>
                                            <span class="body-1-regular-2">Monthly hours compared across available years.</span>
                                        </div>

                                        <div class="report-state" *ngIf="kitchen.comparisonRows.length === 0">
                                            <span class="body-1-regular-2">No year comparison data is available yet.</span>
                                        </div>

                                        <div class="table-wrap" *ngIf="kitchen.comparisonRows.length > 0">
                                            <table class="report-table">
                                                <thead>
                                                    <tr>
                                                        <th>Month</th>
                                                        <th *ngFor="let year of kitchen.comparisonYears; trackBy: trackByIndex">{{ year }}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr *ngFor="let row of kitchen.comparisonRows; trackBy: trackByIndex">
                                                        <th>{{ row.monthLabel }}</th>
                                                        <td *ngFor="let value of row.values; trackBy: trackByIndex">
                                                            {{ formatMetricValue(value.hours, true) }}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </mat-expansion-panel>
                            </mat-accordion>
                        </div>
                    </mat-tab>

                    <mat-tab label="Storage">
                        <div class="report-tab-body" fxLayout="column" fxLayoutGap="20px">
                            <div fxLayout="column" fxLayoutGap="6px">
                                <span class="heading-default">Storage occupancy</span>
                                <span class="body-1-regular-2">Open an area to view its current storage usage snapshot.</span>
                            </div>

                            <mat-accordion [multi]="true" *ngIf="storageAreas.length > 0">
                                <mat-expansion-panel *ngFor="let area of storageAreas; let areaIndex = index; trackBy: trackByIndex"
                                    [expanded]="areaIndex === 0">
                                    <mat-expansion-panel-header>
                                        <mat-panel-title>{{ area.spaceName }}</mat-panel-title>
                                        <mat-panel-description>{{ area.items.length }} storage lines</mat-panel-description>
                                    </mat-expansion-panel-header>

                                    <div class="panel-content">
                                        <div class="table-wrap">
                                            <table class="report-table">
                                                <thead>
                                                    <tr>
                                                        <th>Storage Area</th>
                                                        <th>Total Number of Shelves</th>
                                                        <th>Shelves in Use</th>
                                                        <th>Shelves Available</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr *ngFor="let item of area.items; trackBy: trackByIndex">
                                                        <th>{{ item.storageName }}</th>
                                                        <td>{{ item.totalUnits }}</td>
                                                        <td>{{ item.inUseUnits }}</td>
                                                        <td>{{ item.availableUnits }}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </mat-expansion-panel>
                            </mat-accordion>
                        </div>
                    </mat-tab>
                </mat-tab-group>
            </div>
        </div>
    </div>
</div>
